import { useMemo } from 'react';

import * as hooks from './hooks';
import * as helpers from './helpers';

export const useFormatter = () => {
    const {
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        getDateForCsv,
        getDateForIms,
        getDateTimeLong,
        getDateTimeShort,
        getDateTimeForCsv,
        getDateTimeLongMs,
        getLocaleDateShort,
        parseImsBankIdDate
    } = hooks.useDateFormatter();
    const {
        formatCurrency,
        getCurrencySymbol,
        getIsCurrencySymbolAtTheLeft
    } = hooks.useCurrencyFormatter();
    const { formatNumber, delocalizeNumber } = hooks.useNumberFormatter();

    return useMemo(() => ({
        // region Currency

        /**
         * Formats amount in currency according to options
         * @param amount {number|string} - amount to format
         * @param [options] {Object} - format params
         * @param [options.currencyCode] {string} - currency, if not passed, user's currency is used
         * @param [options.symbolAtTheLeft=true] {string} - 'true' to draw currency symbol to the left of amount
         * @param [options.originalCurrencyCode=true] {string} - 'true' to prevent currency code conversion to currency symbol
         * @param [options.precision=2] {number} - decimal precision
         * @param [options.thSeparator=,] {string} - thousand separator
         * @param [options.decSeparator=.] {string} - decimal separator
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        formatCurrency,
        /**
         * Gets currency symbol to display
         * @param [options] {string|Object} - currency code or currency options, if not passed, user's currency is used
         * @param [options.currencyCode] {string} - currency code
         * @returns {string}
         */
        getCurrencySymbol,
        /**
         * @param [options] {string|Object} - symbolAtTheLeft options
         * @param [options.symbolAtTheLeft] {string}
         * @param [options.locale] {string} - if not passed, user's locale is used
         * @returns {boolean}
         */
        getIsCurrencySymbolAtTheLeft,

        // endregion

        // region Date

        /**
         * Gets date in custom format
         * @param srcDate {*|Date|string}
         * @param [customFormat=date] {*|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        date,
        /**
         * Gets date in 'date' format e.g YYYY-MM-DD
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getDate,
        /**
         * Gets date in 'timeLong' format e.g HH:MM:SS
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getTimeLong,
        /**
         * Gets date in 'timeShort' format e.g HH:MM
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getTimeShort,
        /**
         * Gets date in 'dateTimeLong' format e.g YYYY-MM-DD HH:MM:SS
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getDateTimeLong,
        /**
         * Gets date in 'dateTimeShort' format e.g YYYY-MM-DD HH:MM
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getDateTimeShort,
        /**
         * Gets date in 'dateTimeLongMs' format e.g YYYY-MM-DD HH:MM:SS.sss
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getDateTimeLongMs,
        /**
         * Gets date in '12 Apr 2018' format
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getLocaleDateShort,
        /**
         * Formats date for export to CSV in 'YYYY-MM-DD' format
         * @param srcDate {*|Date|string}
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        getDateForCsv,
        /**
         * Formats date and time for export to CSV in 'YYYY-MM-DD HH:MM' format
         * @param srcDate {*|Date|string}
         * @returns {string}
         */
        getDateTimeForCsv,
        /**
         * Parse date string received from IMS
         * IMS sends us date in non-standard format ('YYYY-MM-DD HH:MM:SS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @param srcDate {string}
         * @returns {Date}
         */
        parseImsDate,
        /**
         * Get date in format 'YYYY-MM-DD HH:MM:SS.sss' in GMT to be sent to IMS (through OGW)
         * @param srcDate {Date}
         * @returns {string}
         */
        getDateForIms,
        /**
         * Parse date string received from getUrlRequest
         * IMS sends us date in non-standard format ('YYYY-MM-DD+HH%3AMM%3ASS.sss') without specifying time zone,
         * but according to IMS documentation, this time always comes in GMT.
         * @memberOf Playtech.API.formatter
         * @function
         * @param scrDate {string}
         * @returns {Date}
         */
        parseImsBankIdDate,

        // endregion

        // region Number

        /**
         * Formats number according to options
         * @param number {number|string} - number to format
         * @param [options] {Object} - format params
         * @param [options.precision=null] {number} - decimal precision
         * @param [options.thSeparator=,] {string} - thousand separator
         * @param [options.decSeparator=.] {string} - decimal separator
         * @param [locale] {string} - if not passed, user's locale is used
         * @returns {string}
         */
        formatNumber,
        /**
         * Convert string, that represents localized number, into number if possible
         * @param str {string} - source string
         * @param [options] {Object} - source format params
         * @param [options.thSeparator=,] {string} - thousand separator
         * @param [options.decSeparator=.] {string} - decimal separator
         * @returns {*|number|string}
         */
        delocalizeNumber,

        // endregion

        // region Other

        /**
         * Get duration in 'HH:MM:SS' format without timezone offset
         * @param duration {Date|string|number}
         * @returns {string}
         */
        getDurationTimeLong: helpers.getDurationTimeLong,
        /**
         * Get duration in 'HH:MM' format without timezone offset
         * @param duration {Date|string|number}
         * @returns {string}
         */
        getDurationTimeShort: helpers.getDurationTimeShort,
        /**
         * Converts seconds to HH:MM:SS or MM:SS
         * @param seconds {string|number}
         * @returns {string}
         */
        formatTimeFromSeconds: helpers.formatTimeFromSeconds

        // endregion
    }), [
        date,
        getDate,
        getTimeLong,
        parseImsDate,
        getTimeShort,
        formatNumber,
        getDateForCsv,
        getDateForIms,
        formatCurrency,
        getDateTimeLong,
        getDateTimeShort,
        delocalizeNumber,
        getDateTimeForCsv,
        getCurrencySymbol,
        getDateTimeLongMs,
        getLocaleDateShort,
        parseImsBankIdDate,
        getIsCurrencySymbolAtTheLeft
    ]);
};

